import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TreatyUsersSearch, UserType } from '../../graphql/generated';

type UseUserManagement = {
  searchParams: TreatyUsersSearch;
  currentPage: number;
  pageSize: number;
  setName: (name?: string) => void;
  setPage: (page: string) => void;
  setUserType: (userType: UserType) => void;
  setIncludeBlocked: (includeBlocked: boolean) => void;
  setRoles: (roles: string[]) => void;
};

const DEFAULT_PAGE_SIZE = 20;

export const useUserManagement = (): UseUserManagement => {
  const [searchParams, setSearchParams] = useSearchParams();

  const name = searchParams.get('name') || '';
  const userType =
    (searchParams.get('userType') as UserType) || UserType.EXTERNAL_USER;
  const includeBlocked: boolean = searchParams.get('includeBlocked') === 'true';
  const page: string = searchParams.get('page') || '1';
  const roles: string[] =
    searchParams.get('roles')?.split(',').filter(Boolean) || [];

  // Set default URL params
  useEffect(() => {
    // Check if any of the required params are missing before setting defaults
    const needsDefaultParams =
      !searchParams.has('userType') ||
      !searchParams.has('includeBlocked') ||
      !searchParams.has('page');

    if (needsDefaultParams) {
      searchParams.set('userType', userType);
      searchParams.set('includeBlocked', String(includeBlocked));
      searchParams.set('page', page);
      // Replace only on setting the defaults
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setName = (name?: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (name) {
      newSearchParams.set('name', name);
      newSearchParams.set('page', '1');
      newSearchParams.set('includeBlocked', 'true');
    } else {
      newSearchParams.delete('name');
      newSearchParams.set('page', '1');
    }
    setSearchParams(newSearchParams);
  };

  const setUserType = (userType: UserType) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('userType', userType);
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  };

  const setIncludeBlocked = (includeBlocked: boolean) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('includeBlocked', String(includeBlocked));
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  };

  const setPage = (page: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page);
    setSearchParams(newSearchParams);
  };

  const setRoles = (roles: string[]) => {
    if (roles.length > 0) {
      searchParams.set('roles', roles.join(','));
    } else {
      searchParams.delete('roles');
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const pageSize = DEFAULT_PAGE_SIZE;
  const currentPage = page ? parseInt(page, 10) : 1;

  return {
    searchParams: {
      name,
      userType,
      includeBlocked,
      roles,
      pagination: {
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
      },
    },
    currentPage,
    pageSize,
    setName,
    setPage,
    setUserType,
    setIncludeBlocked,
    setRoles,
  };
};
